import axios from 'axios';

export const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const api = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  timeout: 15000,
});

// Search places (autocomplete) — backend: GET /api/locations/suggestions
export const searchPlaces = async (query) => {
  try {
    const { data } = await api.get('/locations/suggestions', { params: { query } });
    // location.routes.js returns: [{ id, name, address, placeId }]
    return data;
  } catch (error) {
    console.error('Error searching places:', error);
    return [];
  }
};

// Distance + duration — backend: GET /api/locations/distance?origin=lat,lng&destination=lat,lng
export const calculateDistance = async (origin, destination) => {
  try {
    const { data } = await api.get('/locations/distance', {
      params: { origin, destination },
    });
    return data; // { success, distance(m), duration(s) }
  } catch (error) {
    console.error('Error calculating distance:', error);
    return { success: false, error: 'Failed to calculate distance' };
  }
};

// Place details — backend: GET /api/locations/place-details/:placeId
// Adapted to a stable shape: { location: { lat, lng }, formatted_address, name }
export const getPlaceDetails = async (placeId) => {
  try {
    const { data } = await api.get(`/locations/place-details/${placeId}`);
    return {
      location: data.coordinates,
      formatted_address: data.formattedAddress,
      addressComponents: data.addressComponents,
    };
  } catch (error) {
    console.error('Error getting place details:', error);
    if (error.response?.data?.error === 'LOCATION_OUTSIDE_BIHAR') {
      throw new Error('LOCATION_OUTSIDE_BIHAR');
    }
    return null;
  }
};

// Validate pincode — backend: POST /api/locations/validate-pincode
export const validatePincode = async (pincode) => {
  try {
    const { data } = await api.post('/locations/validate-pincode', { pincode });
    return data; // { valid, message }
  } catch (error) {
    console.error('Error validating pincode:', error);
    return { valid: false, message: 'Could not reach pincode service' };
  }
};

// Location autocomplete suggestions (alias) — same as searchPlaces, returns array
export const fetchSuggestions = searchPlaces;

// Create an anonymous booking (v1 — no auth). Returns { success, bookingCode, booking } or throws.
export const createPublicBooking = async (payload) => {
  const { data } = await api.post('/bookings/public', payload);
  return data;
};

// Fetch a booking by its human-friendly code (e.g. "ZC-A3KP7Q").
export const getPublicBooking = async (code) => {
  const { data } = await api.get(`/bookings/public/${encodeURIComponent(code)}`);
  return data;
};

// Google Maps JS SDK loader — preserved for components that previously imported it.
// Returns a no-op promise; if you later add Maps JS features, plug a real loader here.
export const loadGoogleMapsScript = () => Promise.resolve();
